import { deepmerge } from '@mui/utils';
import ArrowDropDownRounded from '@mui/icons-material/ArrowDropDownRounded';
import { createTheme, ThemeOptions, Theme } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import {
  analyzerNodeBackground,
  dishNodeBackground,
  locationNodeBackground,
  satelliteNodeBackground,
  switchNodeBackground,
  switchNodeDisabledBackground,
} from './features/system-overview/utils/theming';

declare module '@mui/material/styles/createTypography' {
  interface TypographyOptions {
    fontWeightExtraBold?: number;
    fontFamilyCode?: string;
  }

  interface Typography {
    fontWeightExtraBold: number;
    fontFamilyCode: string;
  }
}

const defaultTheme = createTheme();

const systemFont = [
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
];

export const getDesignTokens = (mode: 'light' | 'dark') =>
  ({
    palette: {
      mode: 'light',
      background: { default: '#f1f4f5' },
      text: {
        primary: grey[900],
        secondary: grey[700],
      },
    },
    shape: {
      borderRadius: 10,
    },
    spacing: 10,
    typography: {
      fontFamily: systemFont.join(','),
      fontFamilyCode: [
        'Consolas',
        'Menlo',
        'Monaco',
        'Andale Mono',
        'Ubuntu Mono',
        'monospace',
      ].join(','),
      fontFamilyTagline: ['"PlusJakartaSans-ExtraBold"', ...systemFont].join(
        ','
      ),
      fontFamilySystem: systemFont.join(','),
      fontWeightExtraBold: 800,
      h1: {
        fontFamily: systemFont.join(','),
        fontSize: 'clamp(2.625rem, 1.2857rem + 3.5714vw, 4rem)',
        fontWeight: 800,
        lineHeight: 78 / 70,
      },
      h2: {
        fontFamily: systemFont.join(','),
        fontSize: 'clamp(1.5rem, 0.9643rem + 1.4286vw, 2.25rem)',
        fontWeight: 800,
        lineHeight: 44 / 36,
      },
      h3: {
        fontSize: defaultTheme.typography.pxToRem(36),
        lineHeight: 44 / 36,
        letterSpacing: 0,
      },
      h4: {
        fontSize: defaultTheme.typography.pxToRem(28),
        lineHeight: 42 / 28,
        letterSpacing: 0,
      },
      h5: {
        fontSize: defaultTheme.typography.pxToRem(24),
        lineHeight: 36 / 24,
        letterSpacing: 0,
      },
      h6: {
        fontSize: defaultTheme.typography.pxToRem(20),
        lineHeight: 30 / 20,
        letterSpacing: 0,
      },
      button: {
        textTransform: 'initial',
        fontWeight: 700,
        letterSpacing: 0,
      },
      subtitle1: {
        fontSize: defaultTheme.typography.pxToRem(18),
        lineHeight: 24 / 18,
        letterSpacing: 0,
        fontWeight: 500,
      },
      body1: {
        fontSize: defaultTheme.typography.pxToRem(16), // 16px
        lineHeight: 24 / 16,
        letterSpacing: 0,
      },
      body2: {
        fontSize: defaultTheme.typography.pxToRem(14), // 14px
        lineHeight: 21 / 14,
        letterSpacing: 0,
      },
      caption: {
        display: 'inline-block',
        fontSize: defaultTheme.typography.pxToRem(12), // 12px
        lineHeight: 18 / 12,
        letterSpacing: 0,
        fontWeight: 700,
      },
    },
  } as ThemeOptions);

export function getThemedComponents(theme: Theme) {
  return {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            '*::-webkit-scrollbar': {
              backgroundColor: theme.palette.background.paper,
              borderRadius: '10px',
              width: '12px',
            },
            '*::-webkit-scrollbar-thumb': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.palette.grey[700]
                  : theme.palette.grey[300],
              borderRadius: '10px',
            },
            // Global CSS for system overview page
            '.satelliteNode': {
              fill: satelliteNodeBackground,
              '& ~ .removeBox': {
                '& > *': {
                  fill: '#FF155C',
                },
              },
              '& ~ *': {
                '& > *': {
                  fill: 'black',
                },
              },
            },
            '.dishNode': {
              fill: dishNodeBackground,
              '& ~ .removeBox': {
                '& > *': {
                  fill: '#FF155C',
                },
              },
              '& ~ *': {
                '& > *': {
                  fill: 'black',
                },
              },
            },
            '.switchNode': {
              fill: switchNodeBackground,
              '& ~ .removeBox': {
                '& > *': {
                  fill: '#FF155C',
                },
              },
              '& ~ *': {
                '& > *': {
                  fill: 'black',
                },
              },
            },
            '.switchNodeDisabled': {
              fill: switchNodeDisabledBackground,
              '& ~ .removeBox': {
                '& > *': {
                  fill: '#FF155C',
                },
              },
              '& ~ *': {
                '& > *': {
                  fill: 'black',
                },
              },
            },
            '.analyzerNode': {
              fill: analyzerNodeBackground,
              '& ~ .removeBox': {
                '& > *': {
                  fill: '#FF155C',
                },
              },
              '& ~ *': {
                '& > *': {
                  fill: 'black',
                },
              },
            },
            '.locationNode': {
              fill: locationNodeBackground,
              '& ~ .removeBox': {
                '& > *': {
                  fill: '#FF155C',
                },
              },
              '& ~ *': {
                '& > *': {
                  fill: 'black',
                },
              },
            },
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableTouchRipple: true,
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          sizeLarge: {
            padding: '1rem 1.25rem',
            ...theme.typography.body1,
            lineHeight: 21 / 16,
            fontWeight: 700,
          },
        },
        variants: [
          {
            props: { variant: 'code' },
            style: {
              border: '1px solid',
              ...theme.typography.body2,
              fontFamily: theme.typography.fontFamilyCode,
              fontWeight: 600,
              WebkitFontSmoothing: 'subpixel-antialiased',
            },
          },
        ],
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            [theme.breakpoints.up('md')]: {
              paddingLeft: theme.spacing(2),
              paddingRight: theme.spacing(2),
            },
          },
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'none',
        },
        styleOverrides: {
          root: {
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            '&.MuiTypography-body1 > svg': {
              marginTop: 2,
            },
            '& svg:last-child': {
              marginLeft: 2,
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 5,
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          IconComponent: ArrowDropDownRounded,
        },
        styleOverrides: {
          iconFilled: {
            top: 'calc(50% - .25em)',
          },
        },
      },
      MuiTab: {
        defaultProps: {
          disableTouchRipple: true,
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            '&[href]': {
              textDecorationLine: 'none',
            },
          },
          outlined: {
            display: 'block',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: theme.spacing(1, 2),
          },
          head: {
            fontWeight: 700,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 700,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            paddingTop: 7,
            paddingBottom: 7,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderBottom: '1px solid',
            borderColor: theme.palette.divider,
            boxShadow: 'none',
          },
        },
      },
    },
  };
}

const lightTheme = createTheme(getDesignTokens('light'));
export const brandingLightTheme = deepmerge(
  lightTheme,
  getThemedComponents(lightTheme)
);
